import { useState } from "react";
import {
  FaMap,
  FaSearchLocation,
  FaKey,
  FaBrain,
  FaDatabase,
  FaGlobeAmericas,
} from "react-icons/fa";
import "./Help.css";

const Help = () => {
  const [activeCard, setActiveCard] = useState(null);

  const searchOptions = [
    {
      icon: <FaGlobeAmericas className="option-icon" />,
      title: "Navix",
      steps: [
        "Navigate to Navix",
        "Use Layer controls to show counties and/or RBFS regions (Baseline or Alternative Analysis) or MS2",
        "Click a county or model outline to view matching models",
        "Use the reset view button to return to the default map view",
      ],
      description: "Explore flood models by county with navix.",
    },
    {
      icon: <FaMap className="option-icon" />,
      title: "Map Search",
      steps: [
        "Navigate to map search",
        "Draw a polygon on the map to define an area of interest",
        "Explore models that intersects the drawn area",
      ],
      description: "Search for data models by drawing areas on the map.",
    },
    {
      icon: <FaSearchLocation className="option-icon" />,
      title: "Location Search",
      steps: [
        "Navigate to location search",
        "Type a city or county name and select the suggestion (included County or Texas in suggestion)",
        "Explore models that intersects the selected location",
      ],
      description: "Find data models by searching for specific locations.",
    },
    {
      icon: <FaKey className="option-icon" />,
      title: "Keyword Search",
      steps: [
        "Navigate to keyword search",
        "Choose a category and enter search terms",
        "Explore models",
      ],
      description:
        "Search using specific keywords or phrases to find data models.",
    },
    {
      icon: <FaBrain className="option-icon" />,
      title: (
        <>
          AI Search <span className="experimental-badge">Beta</span>
        </>
      ),
      steps: [
        "Navigate to AI search",
        "Type natural language query",
        "Explore models",
      ],
      description: "Use natural language to search with AI assistance.",
    },
    {
      icon: <FaDatabase className="option-icon" />,
      title: "Datasets Search",
      steps: [
        "Navigate to datasets",
        "Browse available datasets",
        "Apply filters as needed",
        "Explore datasets and documents",
      ],
      description: "Browse and filter through available datasets.",
    },
  ];

  return (
    <div className="help-container">
      <div className="help-header">
        <h1>DMQT Search Options</h1>
        <p className="help-subtitle">
          Learn how to effectively use the Data & Models Query Tool to discover
          and access the data you need.
        </p>
      </div>

      <div className="help-main">
        <div className="search-options-section">
          <h2>&nbsp;</h2>
          <div className="options-grid">
            {searchOptions.map((option) => (
              <div
                key={option.title}
                className="option-card"
                data-active={activeCard === option.title}
                onMouseEnter={() => setActiveCard(option.title)}
                onMouseLeave={() => setActiveCard(null)}
              >
                <div className="option-content">
                  {option.icon}
                  <h3>{option.title}</h3>
                  <p className="option-description">{option.description}</p>
                  <div className="option-steps">
                    {option.steps.map((step) => (
                      <p key={step}>{step}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className="legend-section"
          style={{
            marginTop: "24px",
            marginBottom: "20px",
            background: "#f8f9fa",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
          }}
        >
          <h3 style={{ marginTop: 0 }}>Source Applications</h3>
          <div style={{ marginBottom: "12px" }}>
            <strong>GLO Combined River Basin Flood Studies (RBFS):</strong> The
            General Land Office (GLO) launched the Combined River Basin Flood
            Studies (RBFS) in the 49 counties which received a presidential
            disaster declaration due to the impact of Hurricane Harvey plus 4
            counties in the Lower Rio Grande Valley that received a presidential
            declaration for flooding in 2015. The studies aim to increase
            resiliency by helping communities prioritize effective mitigation
            strategies and pursue additional implementation funds.
            <br />
            For more details regarding RBFS please visit{" "}
            <a
              href="https://www.glo.texas.gov/disaster-recovery/planning-studies/river-basin-study"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.glo.texas.gov/disaster-recovery/planning-studies/river-basin-study
            </a>
          </div>
          <div>
            <strong>TWDB (MS2):</strong> TWDB MS2 is a collaborative project
            between the Texas Water Development Board (TWDB) and the Texas
            Disaster Information System (TDIS) to manage and store hydrologic
            and hydraulic (H&H) models used in flood planning. This system, also
            known as the Model Storage and Management System, is designed to
            ingest, catalog, and store these models, which are essential for
            flood risk mapping and planning efforts.
            <br />
            For more details regarding MS2 please visit{" "}
            <a
              href="https://www.twdb.texas.gov/flood/planning/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.twdb.texas.gov/flood/planning/
            </a>
          </div>
        </div>
      </div>
      <div className="help-footer">
        <p>
          To learn more about TDIS, visit{" "}
          <a href="https://portal.cloud.tdis.io/" target="_blank">
            https://portal.cloud.tdis.io
          </a>{" "}
          <br />
          For any inquiries, please contact{" "}
          <a href="mailto:tdis@tamu.edu">tdis@tamu.edu</a>
        </p>
      </div>
    </div>
  );
};

export default Help;
